/**
 * Utilitaires pour gérer les URLs Supabase Storage
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'http://127.0.0.1:54321';

// Liste des URLs localhost à remplacer par l'URL Supabase actuelle
const LOCALHOST_PATTERNS = [
  'http://127.0.0.1:54321',
  'http://localhost:54321',
  'http://127.0.0.1:3000',
  'http://localhost:3000'
];

/**
 * Convertit une URL relative de la DB en URL publique Supabase Storage
 *
 * Exemples:
 * - "heroes/archer.png" → "http://127.0.0.1:54321/storage/v1/object/public/heroes/archer.png"
 * - "objects/fire_sword.png" → "http://127.0.0.1:54321/storage/v1/object/public/objects/fire_sword.png"
 * - "stories/covers/story1.png" → "http://127.0.0.1:54321/storage/v1/object/public/stories/covers/story1.png"
 * - "/uploads/heroes/hero.png" → "http://127.0.0.1:54321/storage/v1/object/public/heroes/hero.png" (legacy)
 * - "/uploads/general/img.png" → "http://127.0.0.1:54321/storage/v1/object/public/stories/subjects/img.png" (legacy)
 *
 * @param relativePath - Chemin relatif depuis la DB (ex: "heroes/archer.png")
 * @returns URL publique complète Supabase Storage
 */
export const getPublicStorageUrl = (relativePath: string | null | undefined): string => {
  if (!relativePath) {
    return '';
  }

  // Si l'URL est déjà complète (http/https)
  if (relativePath.startsWith('http://') || relativePath.startsWith('https://')) {
    // Remplacer les URLs localhost par l'URL Supabase actuelle (pour mobile)
    for (const localhostUrl of LOCALHOST_PATTERNS) {
      if (relativePath.startsWith(localhostUrl)) {
        const newUrl = relativePath.replace(localhostUrl, SUPABASE_URL);
        console.log('🖼️ URL transformée:', relativePath, '→', newUrl);
        return newUrl;
      }
    }
    // URL externe, la retourner telle quelle
    console.log('🖼️ URL externe (non modifiée):', relativePath);
    return relativePath;
  }

  let supabasePath = relativePath;

  // Convertir les anciennes URLs du backend Node.js vers Supabase Storage
  if (relativePath.startsWith('/uploads/')) {
    // "/uploads/heroes/hero.png" → "heroes/hero.png"
    // "/uploads/objects/obj.png" → "objects/obj.png"
    // "/uploads/general/img.png" → "stories/subjects/img.png"
    // "/uploads/covers/cover.png" → "stories/covers/cover.png"

    const withoutUploads = relativePath.replace(/^\/uploads\//, '');

    // Cas spécial pour "general" qui devient "stories/subjects"
    if (withoutUploads.startsWith('general/')) {
      supabasePath = withoutUploads.replace('general/', 'stories/subjects/');
    } else {
      supabasePath = withoutUploads;
    }
  }

  // Construire l'URL publique Supabase
  const finalUrl = `${SUPABASE_URL}/storage/v1/object/public/${supabasePath}`;
  console.log('🖼️ URL construite:', relativePath, '→', finalUrl);
  return finalUrl;
};

/**
 * Extrait le bucket et le chemin depuis une URL relative
 *
 * @param relativePath - Chemin relatif (ex: "heroes/archer.png")
 * @returns { bucket: string, path: string }
 */
export const parseStoragePath = (relativePath: string): { bucket: string; path: string } => {
  const parts = relativePath.split('/');
  const bucket = parts[0];
  const path = parts.slice(1).join('/');

  return { bucket, path };
};

/**
 * Construit une URL relative pour la DB à partir d'un bucket et d'un chemin
 *
 * @param bucket - Nom du bucket (heroes, objects, stories)
 * @param path - Chemin du fichier (ex: "archer.png" ou "covers/story1.png")
 * @returns URL relative pour la DB
 */
export const buildStoragePath = (bucket: string, path: string): string => {
  return `${bucket}/${path}`;
};
